// BFF tRPC → headless-transport client adapter (fn-69.2/.4).
//
// The headless driver speaks a narrow structural slice (`HeadlessTrpcClient`)
// of the console's vanilla BFF tRPC client. The BFF procedures parse their
// input from `unknown` (plain parser fns, not Zod) so the client's input
// types are `unknown`-wide and accept the typed calls; we narrow the outputs
// to the driver's row/event shapes. Shared by the chat-mode start path
// (fn-69.4) and the agent try-session. (It also served the Archie headless
// run, fn-69.3, until that page was removed.)

import type { BffRouter } from '@sb-ai-platform/bff';
import type { CreateTRPCClient } from '@trpc/client';

import type {
  HeadlessApprovalRow,
  HeadlessDecisionResult,
  HeadlessRunEvent,
  HeadlessTrpcClient,
} from './transport.js';

/** Adapt the console's vanilla BFF tRPC client to the headless transport slice. */
export function toHeadlessClient(client: CreateTRPCClient<BffRouter>): HeadlessTrpcClient {
  return {
    runs: {
      start: {
        mutate: (input) =>
          client.runs.start.mutate(input) as Promise<{ id: string; status: string }>,
      },
      byId: {
        query: (input) => client.runs.byId.query(input) as Promise<{ id: string; status: string }>,
      },
      events: {
        query: (input) =>
          client.runs.events.query(input) as unknown as Promise<{
            data: readonly HeadlessRunEvent[];
            next_cursor?: string | null;
          }>,
      },
      sendMessage: { mutate: (input) => client.runs.sendMessage.mutate(input) },
    },
    approvals: {
      list: {
        query: (input) =>
          client.approvals.list.query(input) as unknown as Promise<{
            data: readonly HeadlessApprovalRow[];
            next_cursor?: string | null;
          }>,
      },
      decide: {
        mutate: (input) => client.approvals.decide.mutate(input) as Promise<HeadlessDecisionResult>,
      },
    },
  };
}
